// BL-2508 — the rebuilt settlements' routes (PRJ-94). The first is the read:
// one settlement with its derived status and every version (ADR 065 — Money
// Model § 12). No verb deletes a settlement or a version (AC 6): Void is the
// only exit, and only a draft is discarded, through the version engine.
import { responseType } from '@auctogon/core/api/registry';
import type { ListPayload } from '@auctogon/core/api';
import { defineAdminRoute, type TenantSource } from './define-admin-route';
// Type-only imports (erased at build) — keep the registry client-bundle-safe.
import type { RecordAssignCheckDto, RecordAssignDto } from '../../schemas/responses/record-assignment';
import type { SettlementChecklistApplyDto, SettlementChecklistCheckDto } from '../../schemas/responses/settlement-checklist-write';
import type {
  RecordAttachmentArchivedDto,
  RecordAttachmentDownloadDto,
  SettlementAttachmentDto,
} from '../../schemas/responses/record-attachment';
import type { LedgerEntryDto } from '../../schemas/responses/ledger-entry';
import type { SettlementDetailDto } from '../../schemas/responses/settlement-detail';
import type { SettlementEditReviewDto, SettlementEditSavedDto } from '../../schemas/responses/settlement-edit';
import type { SettlementListCountsDto, SettlementListRowDto } from '../../schemas/responses/settlement-list';
import type { SettlementSalesRepDto } from '../../schemas/responses/settlement-sales-rep';
import type { SettlementSellerChangeCheckDto } from '../../schemas/responses/settlement-seller-change';
import type { SettlementSendCheckDto, SettlementSendDto } from '../../schemas/responses/settlement-send';
import type { SettlementTransitionCheckDto, SettlementTransitionDto } from '../../schemas/responses/settlement-transition';

/**
 * The Settlements list's search and filter params (BL-2516), read by the list
 * and its counts through the one parser (`parseSettlementListFilters`).
 */
const SETTLEMENT_LIST_FILTER_QUERY = {
  search: '',
  status: '',
  payment: '',
  auction: '',
  assignee: '',
  liens: '',
  created: '',
  created_range: '',
  // BL-2515 — the picked quick-view tile.
  view: '',
  // BL-2533 — one seller's settlements (a customer's Accounting tab, the Seller chip).
  seller: '',
  // BL-2603 — the Checklist filter: the picked house items, and Unchecked (default) or Checked.
  checklist: '',
  checklist_state: '',
};

/** An attachment named by `:attachmentId`, which must belong to the `:id` settlement (BL-2604). */
const ATTACHMENT_IN_SETTLEMENT = {
  from: 'record',
  table: 'settlement_attachment',
  param: 'attachmentId',
  within: { column: 'settlementId', param: 'id' },
} as const satisfies TenantSource;

export const settlements = {
  // BL-2513 — the Settlements list: one page of the house's settlements, sorted,
  // searched and filtered on the server (ADR 059 — Search Spine; BL-2516), and
  // narrowed to one quick-view tile by `view` (BL-2515).
  index: defineAdminRoute({
    path: '/api/settlements',
    params: {},
    methods: {
      GET: {
        grant: 'settlement.read',
        tenant: { from: 'input', field: 'tenantId', in: 'query' },
        response: responseType<ListPayload<SettlementListRowDto>>(),
        query: { tenantId: '', page: '', pageSize: '', sortField: '', sortDir: '', ...SETTLEMENT_LIST_FILTER_QUERY },
      },
    },
  }),
  // BL-2515 + BL-2516 (list-search.md row 15) — the one counts route: each tile's
  // count and Net payout total, and every filter option's count, under the other
  // picks and the search.
  counts: defineAdminRoute({
    path: '/api/settlements/counts',
    params: {},
    methods: {
      GET: {
        grant: 'settlement.read',
        tenant: { from: 'input', field: 'tenantId', in: 'query' },
        response: responseType<SettlementListCountsDto>(),
        query: { tenantId: '', ...SETTLEMENT_LIST_FILTER_QUERY },
      },
    },
  }),
  detail: defineAdminRoute({
    path: '/api/settlements/:id',
    params: { id: '' },
    methods: {
      // Another house's settlement is the gate's 404; a Void one still reads.
      GET: {
        grant: 'settlement.read',
        tenant: { from: 'record', table: 'settlement', param: 'id' },
        response: responseType<SettlementDetailDto>(),
      },
    },
  }),
  // BL-2522 / BL-2648 / BL-2538 — check and uncheck checklist items on one or
  // more settlements, all or nothing over `settlementIds` (ADR 026 — Server
  // Orchestration, Rule 1b). The settlement page's and the list cell's tick is
  // a bulk of one; the Settlements list's bulk Checklist (SLIST-7) is the same
  // pair. A finished Preparing list opens only for the ids the operator's Open
  // settlement names (ADR 033 — Transition Service). Every id must be a
  // settlement of one tenant the caller belongs to; another tenant's id is a
  // 404 at the gate. `max` matches SETTLEMENT_CHECKLIST_EDIT_MAX_IDS.
  checklist: defineAdminRoute({
    path: '/api/settlements/checklist',
    params: {},
    methods: {
      POST: {
        grant: 'settlement.update',
        tenant: { from: 'records', table: 'settlement', field: 'settlementIds', max: 100 },
        response: responseType<SettlementChecklistApplyDto>(),
      },
    },
  }),
  // BL-2538 — the Checklist action's read-only half: the selection's lists and
  // whether it can take an edit (a Void settlement blocks it, AC 7).
  checklist_check: defineAdminRoute({
    path: '/api/settlements/checklist-check',
    params: {},
    methods: {
      POST: {
        grant: 'settlement.update',
        tenant: { from: 'records', table: 'settlement', field: 'settlementIds', max: 100 },
        response: responseType<SettlementChecklistCheckDto>(),
      },
    },
  }),
  // BL-2530 — the settlement statement PDF: the current version, as the page
  // shows it, on US Letter. Gated on the page's own READ grant: printing changes
  // nothing (ADR 062 — Access Grants). The success body is a file
  // (application/pdf), so the verb declares no response DTO; failures keep the
  // JSON envelope (the invoice PDF's precedent).
  pdf: defineAdminRoute({
    path: '/api/settlements/:id/pdf',
    params: { id: '' },
    methods: {
      GET: { grant: 'settlement.read', tenant: { from: 'record', table: 'settlement', param: 'id' } },
    },
  }),
  // BL-2524 — every settlement status move (Open, Void, Unvoid), all or nothing
  // over `settlementIds` (ADR 026 — Server Orchestration, Rule 1b; ADR 033 —
  // Transition Service, Decision 13). The settlement page is a bulk of one; the
  // Settlements list's bulk Change status (SLIST-6) reuses it. Every id must be
  // a settlement of one tenant the caller belongs to; another tenant's id is a
  // 404 at the gate. `max` matches SETTLEMENT_TRANSITION_MAX_IDS.
  transition: defineAdminRoute({
    path: '/api/settlements/transition',
    params: {},
    methods: {
      POST: {
        grant: 'settlement.update',
        tenant: { from: 'records', table: 'settlement', field: 'settlementIds', max: 100 },
        response: responseType<SettlementTransitionDto>(),
      },
    },
  }),
  // BL-2616 (SETL-9b) — Send to seller, Send vN and Resend: a check then an
  // all-or-nothing apply over `settlementIds` (ADR 026 — Server Orchestration,
  // Rule 1b). The settlement page is a bulk of one; SLIST-6's bulk Send reuses
  // the pair. Emailing a statement is `settlement.send`. Every id must be a
  // settlement of one tenant the caller belongs to; another tenant's id is a
  // 404 at the gate. `max` matches SETTLEMENT_SEND_BATCH_CAP.
  send_check: defineAdminRoute({
    path: '/api/settlements/send-check',
    params: {},
    methods: {
      POST: {
        grant: 'settlement.send',
        tenant: { from: 'records', table: 'settlement', field: 'settlementIds', max: 50 },
        response: responseType<SettlementSendCheckDto>(),
      },
    },
  }),
  send: defineAdminRoute({
    path: '/api/settlements/send',
    params: {},
    methods: {
      POST: {
        grant: 'settlement.send',
        tenant: { from: 'records', table: 'settlement', field: 'settlementIds', max: 50 },
        response: responseType<SettlementSendDto>(),
      },
    },
  }),
  // BL-2524 — the status menu's read-only half: which moves the selection
  // allows, where each lands and its confirm's cautions (ADR 033 Decision 8).
  transition_check: defineAdminRoute({
    path: '/api/settlements/transition-check',
    params: {},
    methods: {
      POST: {
        grant: 'settlement.update',
        tenant: { from: 'records', table: 'settlement', field: 'settlementIds', max: 100 },
        response: responseType<SettlementTransitionCheckDto>(),
      },
    },
  }),
  // BL-2521 — the Edit stage: one change set (pay-to, lots taken off) saved in
  // one request, v1 in place while Preparing, else made live as the next
  // version. A change set with deductions or credits (BL-2528) also needs
  // `settlement.adjust`, checked by the handler; liens join with BL-2531.
  // BL-2563: a Change seller group also writes the lots' buyer-invoice drafts
  // under `settlement.update` alone (both it and `invoice.update` map to roles
  // Admin and Accounting hold today). A role holding one but not the other
  // needs a per-field check here first.
  edit: defineAdminRoute({
    path: '/api/settlements/:id/edit',
    params: { id: '' },
    methods: {
      POST: {
        grant: 'settlement.update',
        tenant: { from: 'record', table: 'settlement', param: 'id' },
        response: responseType<SettlementEditSavedDto>(),
      },
    },
  }),
  // BL-2617: Restore as new version — an older version's content over the lots
  // the settlement holds now, made live as the next version in one request
  // (Dan Saper, 2026-10-20). An editor's write, as Edit's Save is.
  draft_restore: defineAdminRoute({
    path: '/api/settlements/:id/draft/restore',
    params: { id: '' },
    methods: {
      POST: {
        grant: 'settlement.update',
        tenant: { from: 'record', table: 'settlement', param: 'id' },
        response: responseType<SettlementEditSavedDto>(),
      },
    },
  }),
  // "Review your changes" before an opened settlement's save. Read only, but
  // only an editor reviews a save.
  edit_review: defineAdminRoute({
    path: '/api/settlements/:id/edit/review',
    params: { id: '' },
    methods: {
      POST: {
        grant: 'settlement.update',
        tenant: { from: 'record', table: 'settlement', param: 'id' },
        response: responseType<SettlementEditReviewDto>(),
      },
    },
  }),
  // BL-2563 — Change seller's read-only check: the guard's verdict for the
  // selected lots, the invoice each is on and, once picked, the new seller's
  // side. The move itself rides the Edit stage's Save (`edit`), same grant.
  change_seller_check: defineAdminRoute({
    path: '/api/settlements/:id/change-seller-check',
    params: { id: '' },
    methods: {
      POST: {
        grant: 'settlement.update',
        tenant: { from: 'record', table: 'settlement', param: 'id' },
        response: responseType<SettlementSellerChangeCheckDto>(),
      },
    },
  }),
  // BL-2525 (SETL-14) — assign, a check then an all-or-nothing apply over
  // `settlementIds` (ADR 026 — Server Orchestration, Rule 1b), the invoice
  // assign generalized (ADR 044 — Must Consolidate). The settlement page's
  // picker is a bulk of one. Every id must be a settlement of one tenant the
  // caller belongs to; another tenant's id is a 404 at the gate. `max` matches
  // RECORD_ASSIGN_MAX_IDS.
  assign_check: defineAdminRoute({
    path: '/api/settlements/assign-check',
    params: {},
    methods: {
      POST: {
        grant: 'settlement.update',
        tenant: { from: 'records', table: 'settlement', field: 'settlementIds', max: 100 },
        response: responseType<RecordAssignCheckDto>(),
      },
    },
  }),
  assign: defineAdminRoute({
    path: '/api/settlements/assign',
    params: {},
    methods: {
      POST: {
        grant: 'settlement.update',
        tenant: { from: 'records', table: 'settlement', field: 'settlementIds', max: 100 },
        response: responseType<RecordAssignDto>(),
      },
    },
  }),
  // BL-2525 (SETL-14) — the settlement's own sales rep, edited in place on any
  // status and never on a version (ADR 065 — Money Model § 12, D4).
  sales_rep: defineAdminRoute({
    path: '/api/settlements/:id/sales-rep',
    params: { id: '' },
    methods: {
      PATCH: {
        grant: 'settlement.update',
        tenant: { from: 'record', table: 'settlement', param: 'id' },
        response: responseType<SettlementSalesRepDto>(),
      },
    },
  }),
  // BL-2604 (SETL-14b) — files attached to a settlement, Staff only or Share
  // with seller, the settlement twin of the invoice's (BL-2217). Register gates
  // on the settlement; the three `:attachmentId` verbs gate on the attachment
  // itself, within its settlement, so another settlement's or house's
  // attachment — or a removed one — is the gate's 404.
  attachments: defineAdminRoute({
    path: '/api/settlements/:id/attachments',
    params: { id: '' },
    methods: {
      POST: {
        grant: 'settlement.update',
        tenant: { from: 'record', table: 'settlement', param: 'id' },
        response: responseType<SettlementAttachmentDto>(),
      },
    },
  }),
  attachment: defineAdminRoute({
    path: '/api/settlements/:id/attachments/:attachmentId',
    params: { id: '', attachmentId: '' },
    methods: {
      PATCH: { grant: 'settlement.update', tenant: ATTACHMENT_IN_SETTLEMENT, response: responseType<SettlementAttachmentDto>() },
      DELETE: { grant: 'settlement.update', tenant: ATTACHMENT_IN_SETTLEMENT, response: responseType<RecordAttachmentArchivedDto>() },
    },
  }),
  // BL-2534 (PRJ-95 SETL-10; ADR 065 — Money Model § 12.6): Record payment, a
  // manual payout to the seller or one lienholder. A payout is the settlement's
  // money, so it takes the settlement's edit grant, as its outcome stamp does
  // (ledger_entries.ts). 201 with the entry; 200 with the first entry when the
  // body's `idempotencyKey` already recorded the same payout.
  payouts: defineAdminRoute({
    path: '/api/settlements/:id/payouts',
    params: { id: '' },
    methods: {
      POST: {
        grant: 'settlement.update',
        tenant: { from: 'record', table: 'settlement', param: 'id' },
        response: responseType<LedgerEntryDto>(),
      },
    },
  }),
  attachment_download: defineAdminRoute({
    path: '/api/settlements/:id/attachments/:attachmentId/download',
    params: { id: '', attachmentId: '' },
    methods: {
      GET: { grant: 'settlement.read', tenant: ATTACHMENT_IN_SETTLEMENT, response: responseType<RecordAttachmentDownloadDto>() },
    },
  }),
};
